import React, { useState, useEffect } from 'react';
import { db, auth } from '../config/firebase-config';
import { collection, getDocs, query, orderBy, limit, where, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AdminBoostEarnings from './AdminBoostEarnings';

// Import the gamification admin component
import GamificationAdmin from './GamificationAdmin';
// Import the AI fraud detection dashboard
import AIFraudDashboard from './AIFraudDashboard';
// Import the referral system settings
import ReferralSystemSettings from './ReferralSystemSettings';
// Import the Tango balance panel
import TangoBalancePanel from './TangoBalancePanel';

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedMusician, setSelectedMusician] = useState(null);
  
  // NEW: Edit musician modal state
  const [editMusicianModal, setEditMusicianModal] = useState({ open: false, musician: null });
  const [editMusicianData, setEditMusicianData] = useState({});
  
  // SMS Protection state
  const [smsProtectionEnabled, setSmsProtectionEnabled] = useState(true);
  const [smsProtectionLoading, setSmsProtectionLoading] = useState(false);
  const [smsProtectionStatus, setSmsProtectionStatus] = useState('');
  const [smsProtectionLastModified, setSmsProtectionLastModified] = useState(null);
  const [smsProtectionModifiedBy, setSmsProtectionModifiedBy] = useState(null);
  
  // Console debugging state
  const [consoleEnabled, setConsoleEnabled] = useState(false);
  
  // Time filtering
  const [timeFilter, setTimeFilter] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Loading states for actions
  const [actionLoading, setActionLoading] = useState({});
  const [notificationModal, setNotificationModal] = useState({ open: false, musicianId: null, musicianName: '' });
  const [notificationMessage, setNotificationMessage] = useState('');
  
  // Live data states
  const [requests, setRequests] = useState([]);
  const [musicians, setMusicians] = useState([]);
  const [songs, setSongs] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Platform settings state
  const [platformSettings, setPlatformSettings] = useState({
    platformFeePercentage: 0.12, // Default 12% - matches current production setting
    minPaymentAmount: 1.00,
    maxPaymentAmount: 500.00,
    autoCancelTimeout: 24,
    dailyVoucherLimit: 5,
    validVoucherAmounts: [20, 50, 100, 200]
  });
  
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  
  // Business settings - dynamically loaded from platform settings
  // This value comes from platformSettings.platformFeePercentage
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    musicianEarnings: 0,
    platformFees: 0,
    totalPaidOut: 0,
    totalRequests: 0,
    completedRequests: 0,
    activeMusicians: 0,
    avgRequestValue: 0,
    revenueGrowth: 0,
    requestGrowth: 0,
    weeklyData: [],
    statusBreakdown: [],
    filteredRequests: [],
    timeRange: ''
  });

  // Load SMS protection status and platform settings on component mount
  useEffect(() => {
    loadSMSProtectionStatus();
    loadPlatformSettings();
    // Load console enabled state from localStorage
    const savedConsoleState = localStorage.getItem('adminConsoleEnabled') === 'true';
    setConsoleEnabled(savedConsoleState);
  }, []);

  // FIXED: Use HTTP version due to CORS issues with callable functions
  const loadSMSProtectionStatus = async () => {
    try {
      setSmsProtectionLoading(true);
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/getSMSProtectionStatusHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSmsProtectionEnabled(result.protectionEnabled);
        setSmsProtectionLastModified(result.lastModified);
        setSmsProtectionModifiedBy(result.modifiedBy);
      } else {
        throw new Error(result.message || 'Failed to load SMS protection status');
      }
    } catch (error) {
      console.error('Error loading SMS protection status:', error);
      setSmsProtectionStatus('Error loading current status');
    } finally {
      setSmsProtectionLoading(false);
    }
  };

  // FIXED: Use HTTP version due to CORS issues with callable functions
  const handleSMSProtectionToggle = async () => {
    try {
      setSmsProtectionLoading(true);
      setSmsProtectionStatus('');
      
      const newStatus = !smsProtectionEnabled;
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/toggleSMSProtectionHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ enabled: newStatus })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSmsProtectionEnabled(newStatus);
        setSmsProtectionStatus(`SMS protection ${newStatus ? 'enabled' : 'disabled'} successfully`);
        setSmsProtectionLastModified(new Date());
        setSmsProtectionModifiedBy('You');
        
        showNotification('success', `SMS protection ${newStatus ? 'enabled' : 'disabled'} successfully!`);
        
        // Clear status message after 3 seconds
        setTimeout(() => setSmsProtectionStatus(''), 3000);
      } else {
        throw new Error(result.message || 'Failed to toggle SMS protection');
      }
    } catch (error) {
      console.error('Error toggling SMS protection:', error);
      setSmsProtectionStatus('Error: ' + error.message);
      showNotification('error', 'Failed to toggle SMS protection. Please try again.');
    } finally {
      setSmsProtectionLoading(false);
    }
  };

  // Load platform settings
  const loadPlatformSettings = async () => {
    try {
      console.log('Loading platform settings...');
      setSettingsLoading(true);
      const functions = getFunctions();
      const getPlatformSettingsFn = httpsCallable(functions, 'getPlatformSettings');
      
      console.log('Calling getPlatformSettings function...');
      const result = await getPlatformSettingsFn();
      console.log('Settings function response:', result.data);
      
      if (result.data && result.data.success) {
        setPlatformSettings(result.data.settings);
        console.log('Platform settings loaded:', result.data.settings);
      } else {
        console.warn('Settings function returned unsuccessful result:', result.data);
      }
    } catch (error) {
      console.error('Error loading platform settings:', error);
      console.error('Error details:', error.code, error.message);
      
      // Set default settings if loading fails
      setPlatformSettings({
        platformFeePercentage: 0.12, // Default 12% - matches current production setting
        minPaymentAmount: 1.00,
        maxPaymentAmount: 500.00,
        autoCancelTimeout: 24,
        dailyVoucherLimit: 5,
        validVoucherAmounts: [20, 50, 100, 200]
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Save platform settings
  const savePlatformSettings = async (settings) => {
    try {
      console.log('Saving platform settings...', settings);
      setSettingsSaving(true);
      const functions = getFunctions();
      const updatePlatformSettingsFn = httpsCallable(functions, 'updatePlatformSettings');
      
      const result = await updatePlatformSettingsFn({ settings });
      console.log('Save result:', result.data);
      
      if (result.data && result.data.success) {
        setPlatformSettings(result.data.settings);
        showNotification('success', 'Settings saved successfully!');
        console.log('Settings saved successfully');
        return true;
      } else {
        throw new Error(result.data?.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('error', 'Failed to save settings: ' + error.message);
      return false;
    } finally {
      setSettingsSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  // Helper function to set loading state for specific actions
  const setActionLoadingState = (key, isLoading) => {
    setActionLoading(prev => ({ ...prev, [key]: isLoading }));
  };

  // SUCCESS/ERROR notification system
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  // UPDATED: Function to reload admin data after boost - now includes vouchers
  const loadLiveAdminData = async () => {
    try {
      console.log('Reloading admin data after boost...');
      
      // Reload musicians data
      const musiciansRef = collection(db, 'musicians');
      const musiciansSnapshot = await getDocs(musiciansRef);
      const musiciansData = musiciansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMusicians(musiciansData);
      
      // Reload earnings data
      const earningsRef = collection(db, 'earnings');
      const earningsSnapshot = await getDocs(earningsRef);
      const earningsData = earningsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      setEarnings(earningsData);

      // NEW: Reload vouchers data to track total paid out
      const vouchersRef = collection(db, 'vouchers');
      const vouchersSnapshot = await getDocs(vouchersRef);
      const vouchersData = vouchersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      setVouchers(vouchersData);
      
      console.log('Admin data reloaded successfully');
    } catch (error) {
      console.error('Error reloading admin data:', error);
    }
  };

  // NEW: Open edit musician modal
  const openEditMusicianModal = (musician) => {
    setEditMusicianData({
      realName: musician.realName || '',
      displayName: musician.displayName || '',
      email: musician.email || '',
      phoneNumber: musician.phoneNumber || '',
      bio: musician.bio || '',
      genre: musician.genre || '',
      location: musician.location || '',
      averageRating: musician.averageRating || 0,
      totalRatings: musician.totalRatings || 0,
      totalEarnings: musician.totalEarnings || 0,
      isActive: musician.isActive !== false,
      status: musician.status || 'active'
    });
    setEditMusicianModal({ open: true, musician });
  };

  // NEW: Save musician profile changes (admin function)
  const saveMusicianProfile = async () => {
    if (!editMusicianModal.musician) return;

    const actionKey = 'edit_musician_profile';
    setActionLoadingState(actionKey, true);

    try {
      console.log('Admin updating musician profile:', editMusicianModal.musician.id);

      // Call Cloud Function for admin profile update
      const functions = getFunctions();
      const adminUpdateMusicianProfile = httpsCallable(functions, 'adminUpdateMusicianProfile');

      const result = await adminUpdateMusicianProfile({
        musicianId: editMusicianModal.musician.id,
        profileData: {
          ...editMusicianData,
          // Ensure numbers are properly formatted
          averageRating: parseFloat(editMusicianData.averageRating) || 0,
          totalRatings: parseInt(editMusicianData.totalRatings) || 0,
          totalEarnings: parseFloat(editMusicianData.totalEarnings) || 0
        },
        adminNote: 'Profile updated via admin dashboard'
      });

      if (result.data.success) {
        // Update local state
        setMusicians(prev => prev.map(musician => 
          musician.id === editMusicianModal.musician.id 
            ? { ...musician, ...editMusicianData, lastUpdated: new Date() }
            : musician
        ));

        showNotification('success', 'Musician profile updated successfully!');
        setEditMusicianModal({ open: false, musician: null });
        setEditMusicianData({});

        // Reload data to ensure consistency
        await loadLiveAdminData();

        console.log('Musician profile updated successfully');
      } else {
        throw new Error(result.data.error || 'Profile update failed');
      }

    } catch (error) {
      console.error('Error updating musician profile:', error);
      
      if (error.code === 'functions/permission-denied') {
        showNotification('error', 'Admin access denied. Please check your admin permissions.');
      } else if (error.code === 'functions/not-found') {
        showNotification('error', 'Cloud Function not found. Please deploy the adminUpdateMusicianProfile function.');
      } else {
        showNotification('error', 'Failed to update musician profile. Please try again.');
      }
    } finally {
      setActionLoadingState(actionKey, false);
    }
  };

  // ADMIN ACTION: Suspend/Activate Musician with Confirmation and Reason
  const toggleMusicianStatus = async (musicianId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const actionKey = `musician_${musicianId}`;
    
    // Find musician name for confirmation dialog
    const musician = musicians.find(m => m.id === musicianId);
    const musicianName = musician?.displayName || musician?.name || 'Unknown Musician';
    
    let reason = '';
    
    if (newStatus === 'suspended') {
      // For suspension, ask for reason first
      reason = prompt(
        `Warning: SUSPENSION REASON for ${musicianName}\n\n` +
        `Please provide a reason for suspending this musician:\n` +
        `(This will be included in their SMS notification)\n\n` +
        `Examples:\n` +
        `• Violation of terms of service\n` +
        `• Customer complaints about behavior\n` +
        `• Inappropriate content\n` +
        `• Failed to respond to requests\n\n` +
        `Enter reason:`
      );
      
      if (reason === null) {
        // User clicked cancel
        return;
      }
      
      if (!reason.trim()) {
        showNotification('error', 'Suspension reason is required. Please provide a reason for the suspension.');
        return;
      }
      
      // Show confirmation dialog with reason
      const confirmMessage = 
        `Warning: CONFIRM SUSPENSION of ${musicianName}\n\n` +
        `Reason: "${reason.trim()}"\n\n` +
        `This will:\n` +
        `• Stop them from receiving new song requests\n` +
        `• Send them an SMS with the suspension reason\n` +
        `• Can be reversed later\n\n` +
        `Type "SUSPEND" to confirm:`;
      
      const userInput = prompt(confirmMessage);
      
      if (userInput !== 'SUSPEND') {
        if (userInput !== null) {
          showNotification('error', 'Please type "SUSPEND" exactly to confirm this action.');
        }
        return;
      }
    } else {
      // For activation, ask for optional reason
      reason = prompt(
        `Check: ACTIVATION REASON for ${musicianName}\n\n` +
        `Optionally provide a reason for reactivating this musician:\n` +
        `(This will be included in their SMS notification)\n\n` +
        `Examples:\n` +
        `• Issue resolved\n` +
        `• Appeal approved\n` +
        `• Misunderstanding cleared up\n\n` +
        `Enter reason (or leave blank):`
      ) || '';
      
      if (reason === null) {
        // User clicked cancel
        return;
      }
      
      // Show confirmation dialog
      const confirmMessage = 
        `Check: CONFIRM ACTIVATION of ${musicianName}\n\n` +
        (reason.trim() ? `Reason: "${reason.trim()}"\n\n` : '') +
        `This will:\n` +
        `• Allow them to receive song requests again\n` +
        `• Send them an SMS notification\n\n` +
        `Type "ACTIVATE" to confirm:`;
      
      const userInput = prompt(confirmMessage);
      
      if (userInput !== 'ACTIVATE') {
        if (userInput !== null) {
          showNotification('error', 'Please type "ACTIVATE" exactly to confirm this action.');
        }
        return;
      }
    }
    
    setActionLoadingState(actionKey, true);
    
    try {
      console.log(`${newStatus === 'active' ? 'Activating' : 'Suspending'} musician:`, musicianId);
      
      // Use Cloud Function instead of direct Firestore update for better security
      const functions = getFunctions();
      const manageMusicianStatus = httpsCallable(functions, 'manageMusicianStatus');
      
      const result = await manageMusicianStatus({
        musicianId,
        action: newStatus === 'suspended' ? 'suspend' : 'activate',
        reason: reason.trim() || 'Administrative decision'
      });
      
      if (result.data.success) {
        // Update local state immediately
        setMusicians(prev => prev.map(musician => 
          musician.id === musicianId 
            ? { ...musician, status: newStatus }
            : musician
        ));
        
        const notificationMsg = result.data.notificationSent 
          ? `Musician ${newStatus}d successfully and notified via SMS!`
          : `Musician ${newStatus}d successfully (no phone number for SMS)!`;
        
        showNotification('success', notificationMsg);
        
        console.log(`Musician ${newStatus} successfully via Cloud Function`);
      } else {
        throw new Error(result.data.error || 'Cloud Function returned failure');
      }
      
    } catch (error) {
      console.error('Error updating musician status:', error);
      
      if (error.code === 'functions/permission-denied') {
        showNotification('error', 'Admin access denied. Please check your admin permissions.');
      } else if (error.code === 'functions/not-found') {
        showNotification('error', 'Cloud Function not found. Please deploy the manageMusicianStatus function.');
      } else {
        showNotification('error', `Failed to ${newStatus === 'active' ? 'activate' : 'suspend'} musician. Please try again.`);
      }
    } finally {
      setActionLoadingState(actionKey, false);
    }
  };

  // ADMIN ACTION: Cancel Request and Process Refund
  const cancelRequestWithRefund = async (requestId, paymentIntentId) => {
    const actionKey = `request_${requestId}`;
    setActionLoadingState(actionKey, true);
    
    try {
      console.log('Cancelling request and processing refund:', requestId);
      
      // Call Cloud Function to handle cancellation and refund
      const functions = getFunctions();
      const adminCancelRequest = httpsCallable(functions, 'adminCancelRequest');
      
      const result = await adminCancelRequest({
        requestId,
        paymentIntentId,
        reason: 'Admin cancellation',
        refundAmount: 'full' // or could be partial
      });
      
      if (result.data.success) {
        // Update local state
        setRequests(prev => prev.map(request => 
          request.id === requestId 
            ? { ...request, status: 'cancelled', cancelledBy: 'admin', cancelledAt: new Date() }
            : request
        ));
        
        showNotification('success', 'Request cancelled and refund processed successfully!');
        setSelectedRequest(null);
        
        console.log('Request cancelled and refund processed');
      } else {
        throw new Error(result.data.error || 'Cancellation failed');
      }
      
    } catch (error) {
      console.error('Error cancelling request:', error);
      showNotification('error', 'Failed to cancel request. Please try again.');
    } finally {
      setActionLoadingState(actionKey, false);
    }
  };

  // ADMIN ACTION: Export Financial Report to CSV
  const exportFinancialReport = () => {
    try {
      console.log('Generating financial report...');
      
      const { startDate, endDate } = getDateRange();
      const filteredRequests = requests.filter(req => {
        const reqDate = req.timestamp;
        return reqDate >= startDate && reqDate <= endDate;
      });
      
      // Prepare CSV data
      const csvData = [
        ['Financial Report - ' + analytics.timeRange],
        ['Generated on: ' + new Date().toLocaleString()],
        [''],
        ['SUMMARY'],
        ['Platform Revenue', formatCurrency(analytics.platformFees)],
        ['Lifetime Requests Value', formatCurrency(analytics.lifetimeRequestsValue)],
        ['Outstanding Balance', formatCurrency(analytics.outstandingBalance)],
        ['Total Paid Out (Vouchers)', formatCurrency(analytics.totalPaidOut)],
        ['Completed Requests', analytics.completedRequests],
        ['Total Requests', analytics.totalRequests],
        ['Average Request Value', formatCurrency(analytics.avgRequestValue)],
        [''],
        ['DETAILED REQUESTS'],
        ['Request ID', 'Customer', 'Musician', 'Song', 'Artist', 'Amount', 'Status', 'Date', 'Platform Fee', 'Musician Earnings']
      ];
      
      // Add request details
      filteredRequests.forEach(request => {
        const platformFee = (request.amount || 0) * platformSettings.platformFeePercentage;
        const musicianEarning = (request.amount || 0) - platformFee;
        
        csvData.push([
          request.id,
          request.customerName || 'Anonymous',
          request.musicianName || 'Unknown',
          request.songName || 'Unknown Song',
          request.artist || 'Unknown Artist',
          request.amount || 0,
          request.status || 'pending',
          request.timestamp ? request.timestamp.toLocaleDateString() : 'Unknown',
          request.status === 'completed' ? platformFee.toFixed(2) : '0.00',
          request.status === 'completed' ? musicianEarning.toFixed(2) : '0.00'
        ]);
      });
      
      // Convert to CSV string
      const csvContent = csvData.map(row => 
        row.map(field => `"${field}"`).join(',')
      ).join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `financial-report-${analytics.timeRange.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('success', 'Financial report exported successfully!');
      console.log('Financial report exported');
      
    } catch (error) {
      console.error('Error exporting report:', error);
      showNotification('error', 'Failed to export report. Please try again.');
    }
  };

  // ADMIN ACTION: Send Notification to Musician
  const sendNotificationToMusician = async () => {
    if (!notificationMessage.trim()) {
      showNotification('error', 'Please enter a notification message.');
      return;
    }
    
    const actionKey = 'send_notification';
    setActionLoadingState(actionKey, true);
    
    try {
      console.log('Sending notification to musician:', notificationModal.musicianId);
      
      // Call Cloud Function to send notification
      const functions = getFunctions();
      const sendAdminNotification = httpsCallable(functions, 'sendAdminNotification');
      
      const result = await sendAdminNotification({
        musicianId: notificationModal.musicianId,
        message: notificationMessage.trim(),
        type: 'admin_notification',
        priority: 'normal'
      });
      
      if (result.data.success) {
        showNotification('success', `Notification sent to ${notificationModal.musicianName} successfully!`);
        setNotificationModal({ open: false, musicianId: null, musicianName: '' });
        setNotificationMessage('');
        console.log('Notification sent successfully');
      } else {
        throw new Error(result.data.error || 'Failed to send notification');
      }
      
    } catch (error) {
      console.error('Error sending notification:', error);
      showNotification('error', 'Failed to send notification. Please try again.');
    } finally {
      setActionLoadingState(actionKey, false);
    }
  };

  // Calculate date range based on filter
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (timeFilter) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = now;
        break;
      case 'quarter':
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterMonth, 1);
        endDate = now;
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = now;
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(0);
        endDate = customEndDate ? new Date(customEndDate) : now;
        break;
      case 'all':
      default:
        startDate = new Date(0);
        endDate = now;
        break;
    }
    
    return { startDate, endDate };
  };

  // Load live data from Firebase
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        console.log('Loading live admin data...');

        const requestsRef = collection(db, 'requests');
        const requestsQuery = query(requestsRef, orderBy('timestamp', 'desc'), limit(1000));
        
        const unsubscribeRequests = onSnapshot(requestsQuery, (snapshot) => {
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
          }));
          console.log('Loaded requests:', requestsData.length);
          setRequests(requestsData);
        });

        const musiciansRef = collection(db, 'musicians');
        const musiciansSnapshot = await getDocs(musiciansRef);
        const musiciansData = musiciansSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded musicians:', musiciansData.length);
        setMusicians(musiciansData);

        const songsRef = collection(db, 'songs');
        const songsSnapshot = await getDocs(songsRef);
        const songsData = songsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded songs:', songsData.length);
        setSongs(songsData);

        const earningsRef = collection(db, 'earnings');
        const earningsSnapshot = await getDocs(earningsRef);
        const earningsData = earningsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
        console.log('Loaded earnings:', earningsData.length);
        setEarnings(earningsData);

        // NEW: Load vouchers data to track total paid out
        const vouchersRef = collection(db, 'vouchers');
        const vouchersSnapshot = await getDocs(vouchersRef);
        const vouchersData = vouchersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
        console.log('Loaded vouchers:', vouchersData.length);
        setVouchers(vouchersData);

        setLoading(false);

        return () => {
          unsubscribeRequests();
        };

      } catch (error) {
        console.error('Error loading admin data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // FIXED: Calculate analytics from live data - NOW USES MUSICIAN PROFILES FOR EARNINGS
  useEffect(() => {
    if (requests.length > 0 || musicians.length > 0 || vouchers.length > 0) {
      console.log('FIXED FINANCIAL ANALYTICS CALCULATION - Using Musician Profiles');
      console.log('==========================================');
      
      // 1. Date filtering
      const { startDate, endDate } = getDateRange();
      const filteredRequests = requests.filter(req => {
        const reqDate = req.timestamp;
        return reqDate >= startDate && reqDate <= endDate;
      });
      
      console.log(`FILTERED REQUESTS: ${filteredRequests.length} out of ${requests.length}`);
      
      // 2. Calculate revenue from completed requests (for platform fees)
      // FIXED (correct)
      const revenueGeneratingStatuses = [
        'completed', 'finished'
      ];
      
      const revenueRequests = filteredRequests.filter(r => {
        const status = (r.status || '').toLowerCase();
        return revenueGeneratingStatuses.some(s => s === status) && r.amount && r.amount > 0;
      });
      
      const totalRequestRevenue = revenueRequests.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
      const platformFees = totalRequestRevenue * platformSettings.platformFeePercentage;
      
      console.log(`REQUEST REVENUE: $${totalRequestRevenue}, PLATFORM FEES: $${platformFees}`);
      
      // UPDATED: Calculate OUTSTANDING BALANCE using musician profiles (includes boosts)
      let outstandingBalance = 0;
      let activeMusicians = 0;
      
      console.log('CALCULATING OUTSTANDING BALANCE FROM MUSICIAN PROFILES:');
      musicians.forEach(musician => {
        // Use totalEarnings from musician profile (includes boosts/deductions)
        const musicianEarnings = musician.totalEarnings || 0;
        outstandingBalance += musicianEarnings;
        
        console.log(`  - ${musician.displayName || musician.id}: $${musicianEarnings}`);
        
        if (musician.status !== 'suspended' && musician.isActive !== false) {
          activeMusicians++;
        }
      });
      
      console.log(`TOTAL OUTSTANDING BALANCE (from musician profiles): $${outstandingBalance}`);
      
      // Calculate total paid out via vouchers
      const totalPaidOut = vouchers
        .filter(voucher => voucher.status === 'issued' || voucher.status === 'delivered' || voucher.status === 'used')
        .reduce((sum, voucher) => sum + (parseFloat(voucher.amount) || 0), 0);
      
      console.log(`TOTAL PAID OUT (vouchers): $${totalPaidOut}`);
      
      // Outstanding balance is now what matters (not net position)
      const actualOutstanding = outstandingBalance - totalPaidOut;
      
      console.log(`ACTUAL OUTSTANDING: $${actualOutstanding} (${outstandingBalance} owed - ${totalPaidOut} paid out)`);
      
      // 3. Status breakdown
      const statusCounts = {};
      filteredRequests.forEach(req => {
        const status = req.status || 'undefined';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      
      // 4. Other metrics
      const totalRequests = filteredRequests.length;
      const avgRequestValue = revenueRequests.length > 0 ? totalRequestRevenue / revenueRequests.length : 0;

      // 5. Time range
      let timeRange = '';
      if (timeFilter === 'custom') {
        timeRange = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
      } else if (timeFilter === 'all') {
        timeRange = 'All Time';
      } else {
        timeRange = `This ${timeFilter.charAt(0).toUpperCase() + timeFilter.slice(1)}`;
      }

      // 6. Status breakdown for UI
      const statusBreakdown = [
        { 
          name: 'Completed', 
          value: (statusCounts.completed || 0) + (statusCounts.finished || 0), 
          color: '#10B981' 
        },
        { 
          name: 'Performing', 
          value: (statusCounts.performing || 0) + (statusCounts.preparing || 0), 
          color: '#F59E0B' 
        },
        { 
          name: 'Pending', 
          value: (statusCounts.pending || 0) + (statusCounts.authorized || 0), 
          color: '#3B82F6' 
        },
        { 
          name: 'Cancelled', 
          value: statusCounts.cancelled || 0, 
          color: '#EF4444' 
        }
      ];

      // 7. Weekly data calculation
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return date;
      });

      const weeklyData = last7Days.map(date => {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        const dayRequests = filteredRequests.filter(req => {
          const reqDate = req.timestamp;
          return reqDate && reqDate.toDateString() === date.toDateString();
        });
        
        const dayRevenue = dayRequests
          .filter(r => {
            const status = (r.status || '').toLowerCase();
            return revenueGeneratingStatuses.some(s => s === status) && r.amount && r.amount > 0;
          })
          .reduce((sum, r) => sum + (r.amount || 0), 0);

        return {
          name: dayName,
          requests: dayRequests.length,
          revenue: dayRevenue
        };
      });

      console.log('\nFINAL FINANCIAL CALCULATIONS (UPDATED):');
      console.log('Lifetime requests value:', totalRequestRevenue);
      console.log('Platform fees:', platformFees);
      console.log('Outstanding balance (musician profiles including boosts):', outstandingBalance);
      console.log('Total paid out (vouchers):', totalPaidOut);
      console.log('Actual outstanding:', actualOutstanding);
      console.log('Average request value:', avgRequestValue);
      
      // 8. Set the UPDATED analytics state
      const finalAnalytics = {
        lifetimeRequestsValue: totalRequestRevenue, // NEW: Total gross value of all requests
        outstandingBalance: actualOutstanding, // NEW: Current amount owed to musicians
        platformFees: platformFees,
        totalPaidOut: totalPaidOut, // Total vouchers redeemed
        totalRequests: totalRequests,
        completedRequests: revenueRequests.length,
        activeMusicians: activeMusicians,
        avgRequestValue: avgRequestValue,
        revenueGrowth: 12.5,
        requestGrowth: 8.3,
        weeklyData: weeklyData,
        statusBreakdown: statusBreakdown,
        filteredRequests: filteredRequests,
        timeRange: timeRange
      };
      
      console.log('==========================================');
      console.log('END FIXED FINANCIAL ANALYTICS DEBUG');
      
      setAnalytics(finalAnalytics);
    }
  }, [requests, musicians, earnings, vouchers, timeFilter, customStartDate, customEndDate]); // Added vouchers dependency

  const tabStyle = (isActive) => ({
    padding: '12px 24px',
    backgroundColor: isActive ? '#7C3AED' : '#374151',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    marginRight: '8px'
  });

  const cardStyle = {
    backgroundColor: '#1F2937',
    border: '2px solid #374151',
    borderRadius: '12px',
    padding: '24px',
    color: 'white'
  };

  const statCardStyle = {
    ...cardStyle,
    textAlign: 'center',
    background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'performing': return '#F59E0B';
      case 'pending': return '#3B82F6';
      case 'cancelled': return '#EF4444';
      case 'active': return '#10B981';
      case 'suspended': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;
  const formatPercentage = (value) => `${value > 0 ? '+' : ''}${value}%`;
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : timestamp;
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes} mins ago`;
    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  // Notification Component
  const NotificationBanner = () => {
    if (!notification.show) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        padding: '16px 24px',
        backgroundColor: notification.type === 'success' ? '#065F46' : '#7F1D1D',
        border: `3px solid ${notification.type === 'success' ? '#10B981' : '#DC2626'}`,
        borderRadius: '12px',
        color: 'white',
        fontWeight: 'bold',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '20px' }}>
          {notification.type === 'success' ? 'Check' : 'X'}
        </span>
        {notification.message}
      </div>
    );
  };

  // Time Filter Component
  const TimeFilterComponent = () => (
    <div style={{
      ...cardStyle,
      marginBottom: '24px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      alignItems: 'center'
    }}>
      <h3 style={{ margin: 0, color: '#E5E7EB', fontSize: '18px' }}>
        Time Period: {analytics.timeRange}
      </h3>
      
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {[
          { value: 'week', label: 'This Week' },
          { value: 'month', label: 'This Month' },
          { value: 'quarter', label: 'This Quarter' },
          { value: 'year', label: 'This Year' },
          { value: 'all', label: 'All Time' },
          { value: 'custom', label: 'Custom' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => setTimeFilter(option.value)}
            style={{
              padding: '8px 16px',
              backgroundColor: timeFilter === option.value ? '#7C3AED' : '#374151',
              color: 'white',
              border: timeFilter === option.value ? '2px solid #6D28D9' : '2px solid #6B7280',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {timeFilter === 'custom' && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ color: '#9CA3AF', fontSize: '14px', marginRight: '8px' }}>From:</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              style={{
                padding: '8px',
                backgroundColor: '#374151',
                border: '1px solid #6B7280',
                borderRadius: '6px',
                color: 'white'
              }}
            />
          </div>
          <div>
            <label style={{ color: '#9CA3AF', fontSize: '14px', marginRight: '8px' }}>To:</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              style={{
                padding: '8px',
                backgroundColor: '#374151',
                border: '1px solid #6B7280',
                borderRadius: '6px',
                color: 'white'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  // UPDATED: Enhanced Overview Tab with corrected financial metrics
  const OverviewTab = () => {
    return (
      <div>
      <TimeFilterComponent />
      
      {/* UPDATED: Financial Metrics with proper calculations and new "Total Paid Out" */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        
        {/* Platform Revenue (FIRST) */}
        <div style={{
          ...statCardStyle,
          background: 'linear-gradient(135deg, #7C2D12 0%, #EA580C 100%)',
          border: '3px solid #F97316'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#FED7AA', fontSize: '18px' }}>Platform Revenue</h3>
          <div style={{ fontSize: '36px', fontWeight: 'bold', margin: '8px 0', color: 'white' }}>
            {formatCurrency(analytics.platformFees)}
          </div>
          <div style={{ color: '#FED7AA', fontSize: '14px' }}>
            {(platformSettings.platformFeePercentage * 100).toFixed(0)}% fee on completed requests
          </div>
        </div>

        {/* Total Request Revenue (SECOND) */}
        <div style={{
          ...statCardStyle,
          background: 'linear-gradient(135deg, #065F46 0%, #047857 100%)',
          border: '3px solid #10B981'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#A7F3D0', fontSize: '18px' }}>Lifetime Requests Value</h3>
          <div style={{ fontSize: '36px', fontWeight: 'bold', margin: '8px 0', color: 'white' }}>
            {formatCurrency(analytics.lifetimeRequestsValue)}
          </div>
          <div style={{ color: '#A7F3D0', fontSize: '14px' }}>
            From {analytics.completedRequests} completed requests
          </div>
        </div>

        {/* FIXED: Total Liabilities - Now shows musician earnings including boosts */}
        <div style={{
          ...statCardStyle,
          background: 'linear-gradient(135deg, #991B1B 0%, #DC2626 100%)',
          border: '3px solid #EF4444'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#FECACA', fontSize: '18px' }}>Outstanding Balance</h3>
          <div style={{ fontSize: '36px', fontWeight: 'bold', margin: '8px 0', color: 'white' }}>
            {formatCurrency(analytics.outstandingBalance)}
          </div>
          <div style={{ color: '#FECACA', fontSize: '14px' }}>
            Current amount owed to musicians
          </div>
        </div>

        {/* NEW: Total Paid Out via Vouchers */}
        <div style={{
          ...statCardStyle,
          background: 'linear-gradient(135deg, #7C2D87 0%, #A855F7 100%)',
          border: '3px solid #C084FC'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#E9D5FF', fontSize: '18px' }}>Total Paid Out</h3>
          <div style={{ fontSize: '36px', fontWeight: 'bold', margin: '8px 0', color: 'white' }}>
            {formatCurrency(analytics.totalPaidOut)}
          </div>
          <div style={{ color: '#E9D5FF', fontSize: '14px' }}>
            Redeemed via vouchers ({vouchers.length} vouchers)
          </div>
        </div>


        {/* Average Request Value */}
        <div style={{
          ...statCardStyle,
          background: 'linear-gradient(135deg, #581C87 0%, #7C3AED 100%)',
          border: '3px solid #8B5CF6'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#DDD6FE', fontSize: '18px' }}>Avg Request Value</h3>
          <div style={{ fontSize: '36px', fontWeight: 'bold', margin: '8px 0', color: 'white' }}>
            {formatCurrency(analytics.avgRequestValue)}
          </div>
          <div style={{ color: '#DDD6FE', fontSize: '14px' }}>
            Per completed request
          </div>
        </div>

      </div>

      {/* Recent Activity */}
      <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>
          Recent Activity ({analytics.filteredRequests.filter(request => {
            const status = (request.status || '').toLowerCase();
            return ['completed', 'finished'].includes(status);
          }).length} requests in {analytics.timeRange})
        </h3>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {analytics.filteredRequests
            .filter(request => {
              const status = (request.status || '').toLowerCase();
              return ['completed', 'finished'].includes(status);
            })
            .slice(0, 10).map(request => (
              <div key={request.id} style={{
                padding: '12px',
                backgroundColor: '#374151',
                borderRadius: '8px',
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: 'white' }}>
                    {request.songName || 'Unknown Song'} - {request.customerName || 'Anonymous'}
                  </div>
                  <div style={{ color: '#9CA3AF', fontSize: '14px' }}>
                    {request.musicianName || 'Unknown Musician'} • {formatCurrency(request.amount)} • {formatTimestamp(request.timestamp)}
                  </div>
                </div>
                <div style={{
                  padding: '4px 12px',
                  backgroundColor: getStatusColor(request.status),
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {(request.status || 'pending').toUpperCase()}
                </div>
              </div>
            ))}
          {analytics.filteredRequests.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '40px' }}>
              No requests found for the selected time period.
            </div>
          )}
        </div>
      </div>

      {/* Functional Quick Actions */}
      <div style={{ ...cardStyle, gridColumn: 'span 2', marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Admin Actions</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <button 
            onClick={exportFinancialReport}
            style={{
              padding: '12px 16px',
              backgroundColor: '#7C3AED',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Export Financial Report
          </button>
          <button 
            onClick={() => setActiveTab('requests')}
            style={{
              padding: '12px 16px',
              backgroundColor: '#EF4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Manage Requests
          </button>
          <button 
            onClick={() => setActiveTab('musicians')}
            style={{
              padding: '12px 16px',
              backgroundColor: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Manage Musicians
          </button>
          <button 
            onClick={() => setActiveTab('gamification')}
            style={{
              padding: '12px 16px',
              backgroundColor: '#F59E0B',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Gamification
          </button>
          <button style={{
            padding: '12px 16px',
            backgroundColor: '#6B7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
            Platform Settings
          </button>
        </div>
      </div>
    </div>
  );
  };

  // Enhanced Requests Tab with Functional Actions
  const RequestsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <TimeFilterComponent />
      
      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#E5E7EB' }}>Requests Management ({analytics.filteredRequests.length} in {analytics.timeRange})</h3>
          <select style={{
            padding: '8px 12px',
            backgroundColor: '#374151',
            color: 'white',
            border: '1px solid #6B7280',
            borderRadius: '6px'
          }}>
            <option>All Status</option>
            <option>Pending</option>
            <option>Performing</option>
            <option>Completed</option>
            <option>Cancelled</option>
          </select>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #374151' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Request ID</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Customer</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Song</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Musician</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Time</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {analytics.filteredRequests
                .filter(request => {
                  const status = (request.status || '').toLowerCase();
                  return ['completed', 'finished'].includes(status);
                })
                .map(request => (
                  <tr key={request.id} style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px', color: '#9CA3AF', fontFamily: 'monospace' }}>
                      {request.id.substring(0, 8)}...
                    </td>
                    <td style={{ padding: '12px', color: 'white' }}>{request.customerName || 'Anonymous'}</td>
                    <td style={{ padding: '12px', color: 'white' }}>
                      <div>{request.songName || 'Unknown Song'}</div>
                      <div style={{ color: '#9CA3AF', fontSize: '12px' }}>{request.artist || 'Unknown Artist'}</div>
                    </td>
                    <td style={{ padding: '12px', color: 'white' }}>{request.musicianName || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: '#10B981', fontWeight: 'bold' }}>{formatCurrency(request.amount)}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: getStatusColor(request.status),
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: 'white'
                      }}>
                        {(request.status || 'pending').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: '#9CA3AF' }}>{formatTimestamp(request.timestamp)}</td>
                    <td style={{ padding: '12px' }}>
                      <button 
                        onClick={() => setSelectedRequest(request)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {analytics.filteredRequests.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '40px' }}>
              No requests found for the selected time period.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // NEW: Enhanced Musicians Tab with Full Profile Edit Capability
  const MusiciansTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: '#E5E7EB' }}>Musicians Management ({musicians.length} total)</h3>
          <button style={{
            padding: '10px 20px',
            backgroundColor: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
            + Add New Musician
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        {musicians.map(musician => {
          const musicianSongs = songs.filter(song => song.musicianId === musician.id);
          const musicianRequests = requests.filter(req => {
            const status = (req.status || '').toLowerCase();
            return req.musicianId === musician.id && ['completed', 'finished'].includes(status);
          });
          
          // UPDATED: Use totalEarnings from musician profile (includes boosts)
          const musicianEarnings = musician.totalEarnings || 0;

          const isLoading = actionLoading[`musician_${musician.id}`];

          return (
            <div key={musician.id} style={{
              ...cardStyle,
              border: `2px solid ${getStatusColor(musician.status || 'active')}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h4 style={{ margin: '0 0 8px 0', color: 'white', fontSize: '18px' }}>
                    {musician.displayName || musician.name || 'Unknown Musician'}
                  </h4>
                  <div style={{ color: '#9CA3AF', fontSize: '14px' }}>{musician.email}</div>
                  <div style={{ color: '#9CA3AF', fontSize: '14px' }}>
                    {musician.phoneNumber ? '***-***-' + musician.phoneNumber.slice(-4) : 'No phone'}
                  </div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  backgroundColor: getStatusColor(musician.status || 'active'),
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {(musician.status || 'active').toUpperCase()}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ color: '#9CA3AF', fontSize: '12px' }}>Total Earnings</div>
                  <div style={{ color: '#10B981', fontWeight: 'bold', fontSize: '16px' }}>
                    {formatCurrency(musicianEarnings)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#9CA3AF', fontSize: '12px' }}>Average Rating</div>
                  <div style={{ color: '#F59E0B', fontWeight: 'bold', fontSize: '16px' }}>
                    {musician.averageRating || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#9CA3AF', fontSize: '12px' }}>Songs in Repertoire</div>
                  <div style={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>{musicianSongs.length}</div>
                </div>
                <div>
                  <div style={{ color: '#9CA3AF', fontSize: '12px' }}>Total Requests</div>
                  <div style={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>{musicianRequests.length}</div>
                </div>
              </div>

              {/* UPDATED: AdminBoostEarnings component with correct currentEarnings */}
              <AdminBoostEarnings
                musicianId={musician.id}
                currentEarnings={formatCurrency(musicianEarnings).replace(',', '')}
                onBoostSuccess={loadLiveAdminData}
              />

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', marginTop: '16px' }}>
                <button 
                  onClick={() => setSelectedMusician(musician)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  View Profile
                </button>
                {/* NEW: Edit Profile Button */}
                <button 
                  onClick={() => openEditMusicianModal(musician)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    backgroundColor: '#7C3AED',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Edit Profile
                </button>
                <button 
                  onClick={() => toggleMusicianStatus(musician.id, musician.status || 'active')}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    backgroundColor: isLoading ? '#6B7280' : 
                      (musician.status || 'active') === 'active' ? '#EF4444' : '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  {isLoading ? 'Processing...' : 
                   (musician.status || 'active') === 'active' ? 'Suspend' : 'Activate'}
                </button>
              </div>

              <button 
                onClick={() => setNotificationModal({ 
                  open: true, 
                  musicianId: musician.id, 
                  musicianName: musician.displayName || musician.name || 'Unknown' 
                })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Send Notification
              </button>
            </div>
          );
        })}
        {musicians.length === 0 && (
          <div style={{ 
            ...cardStyle, 
            textAlign: 'center', 
            color: '#9CA3AF', 
            gridColumn: 'span 2' 
          }}>
            No musicians found in your database.
          </div>
        )}
      </div>
    </div>
  );

  // FIXED Analytics Tab - NO RECHARTS - Pure HTML/CSS Charts
  const AnalyticsTab = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <TimeFilterComponent />
        
        {/* Weekly Revenue & Requests - HTML Table Instead of Chart */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Weekly Revenue & Requests</h3>
          
          <div style={{ color: 'white' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #374151' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#E5E7EB' }}>Day</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#E5E7EB' }}>Requests</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#E5E7EB' }}>Revenue</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: '#E5E7EB' }}>Visual</th>
                </tr>
              </thead>
              <tbody>
                {analytics.weeklyData?.map((day, index) => {
                  const maxRevenue = Math.max(...analytics.weeklyData.map(d => d.revenue));
                  const maxRequests = Math.max(...analytics.weeklyData.map(d => d.requests));
                  const revenuePercent = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                  const requestsPercent = maxRequests > 0 ? (day.requests / maxRequests) * 100 : 0;
                  
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #374151' }}>
                      <td style={{ padding: '12px', color: 'white', fontWeight: 'bold' }}>{day.name}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#3B82F6', fontWeight: 'bold' }}>{day.requests}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#10B981', fontWeight: 'bold' }}>{formatCurrency(day.revenue)}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '10px', color: '#9CA3AF', marginBottom: '2px' }}>Revenue</div>
                            <div style={{ 
                              width: '100%', 
                              height: '8px', 
                              backgroundColor: '#374151', 
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${requestsPercent}%`,
                                height: '100%',
                                backgroundColor: '#3B82F6',
                                transition: 'width 0.3s ease'
                              }}></div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }) || []}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          
          {/* Request Status Breakdown - Visual Progress Bars */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Request Status Breakdown</h3>
            
            <div style={{ color: 'white' }}>
              {analytics.statusBreakdown?.map((item, index) => (
                <div key={index} style={{ marginBottom: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '6px'
                  }}>
                    <span style={{ color: '#E5E7EB', fontWeight: 'bold' }}>{item.name}</span>
                    <span style={{ color: item.color, fontWeight: 'bold' }}>{item.value}</span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '12px', 
                    backgroundColor: '#374151', 
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: analytics.totalRequests > 0 ? `${(item.value / analytics.totalRequests) * 100}%` : '0%',
                      height: '100%',
                      backgroundColor: item.color,
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#9CA3AF', 
                    marginTop: '2px' 
                  }}>
                    {analytics.totalRequests > 0 ? 
                      `${((item.value / analytics.totalRequests) * 100).toFixed(1)}%` : '0%'
                    } of total requests
                  </div>
                </div>
              )) || []}
            </div>
          </div>

          {/* Platform Metrics */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Platform Metrics</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF' }}>Average Request Value</span>
                <span style={{ color: '#7C3AED', fontWeight: 'bold' }}>
                  {formatCurrency(analytics.avgRequestValue)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF' }}>Total Revenue ({analytics.timeRange})</span>
                <span style={{ color: '#10B981', fontWeight: 'bold' }}>
                  {formatCurrency(analytics.totalRevenue)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Analytics without Charts */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Detailed Analytics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            
            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <div style={{ color: '#3B82F6', fontSize: '28px', fontWeight: 'bold' }}>
                {analytics.completedRequests}
              </div>
              <div style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '4px' }}>Completed Requests</div>
              <div style={{ color: '#10B981', fontSize: '12px', marginTop: '2px' }}>
                {analytics.totalRequests > 0 ? 
                  `${((analytics.completedRequests / analytics.totalRequests) * 100).toFixed(1)}% success rate` : 
                  'No requests yet'
                }
              </div>
            </div>

            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <div style={{ color: '#10B981', fontSize: '28px', fontWeight: 'bold' }}>
                {formatCurrency(analytics.musicianEarnings)}
              </div>
              <div style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '4px' }}>Musician Earnings</div>
              <div style={{ color: '#F59E0B', fontSize: '12px', marginTop: '2px' }}>
                Includes admin boosts/deductions
              </div>
            </div>

            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <div style={{ color: '#EF4444', fontSize: '28px', fontWeight: 'bold' }}>
                {analytics.statusBreakdown.find(s => s.name === 'Cancelled')?.value || 0}
              </div>
              <div style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '4px' }}>Cancelled Requests</div>
              <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '2px' }}>
                {analytics.totalRequests > 0 ? 
                  `${(((analytics.statusBreakdown.find(s => s.name === 'Cancelled')?.value || 0) / analytics.totalRequests) * 100).toFixed(1)}% cancel rate` :
                  'No cancellations'
                }
              </div>
            </div>

            <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <div style={{ color: '#F59E0B', fontSize: '28px', fontWeight: 'bold' }}>
                {analytics.statusBreakdown.find(s => s.name === 'Performing')?.value || 0}
              </div>
              <div style={{ color: '#9CA3AF', fontSize: '14px', marginTop: '4px' }}>Currently Performing</div>
              <div style={{ color: '#F59E0B', fontSize: '12px', marginTop: '2px' }}>
                Live requests in progress
              </div>
            </div>

          </div>
        </div>

        {/* Performance Summary */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Performance Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            
            <div style={{ padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#10B981' }}>Revenue Metrics</h4>
              <div style={{ fontSize: '14px', color: '#E5E7EB', lineHeight: '1.6' }}>
                <div>• Total Revenue: <strong>{formatCurrency(analytics.totalRevenue)}</strong></div>
                <div>• Platform Fees: <strong>{formatCurrency(analytics.platformFees)}</strong></div>
                <div>• Musician Earnings: <strong>{formatCurrency(analytics.musicianEarnings)}</strong></div>
                <div>• Total Paid Out: <strong>{formatCurrency(analytics.totalPaidOut)}</strong></div>
              </div>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#3B82F6' }}>Request Statistics</h4>
              <div style={{ fontSize: '14px', color: '#E5E7EB', lineHeight: '1.6' }}>
                <div>• Total Requests: <strong>{analytics.totalRequests}</strong></div>
                <div>• Completed: <strong>{analytics.completedRequests}</strong></div>
                <div>• Success Rate: <strong>
                  {analytics.totalRequests > 0 ? 
                    `${((analytics.completedRequests / analytics.totalRequests) * 100).toFixed(1)}%` : '0%'
                  }
                </strong></div>
                <div>• Time Period: <strong>{analytics.timeRange}</strong></div>
              </div>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#F59E0B' }}>Platform Health</h4>
              <div style={{ fontSize: '14px', color: '#E5E7EB', lineHeight: '1.6' }}>
                <div>• Active Musicians: <strong>{analytics.activeMusicians}</strong></div>
                <div>• Total Musicians: <strong>{musicians.length}</strong></div>
                <div>• Songs Available: <strong>{songs.length}</strong></div>
                <div>• Platform Status: <strong style={{ color: '#10B981' }}>Operational</strong></div>
              </div>
            </div>

          </div>
        </div>

        {/* Tango Balance Panel */}
        <div style={{ marginTop: '32px' }}>
          <TangoBalancePanel />
        </div>
      </div>
    );
  };

  // UPDATED: Settings Tab with SMS Protection Toggle and Platform Settings
  const SettingsTab = () => {
    console.log('SettingsTab component rendering...');
    
    // Local state for form data
    const [formData, setFormData] = React.useState({
      platformFeePercentage: platformSettings.platformFeePercentage * 100,
      minPaymentAmount: platformSettings.minPaymentAmount,
      maxPaymentAmount: platformSettings.maxPaymentAmount,
      autoCancelTimeout: platformSettings.autoCancelTimeout,
      dailyVoucherLimit: platformSettings.dailyVoucherLimit
    });

    // Update form when platform settings change
    React.useEffect(() => {
      setFormData({
        platformFeePercentage: platformSettings.platformFeePercentage * 100,
        minPaymentAmount: platformSettings.minPaymentAmount,
        maxPaymentAmount: platformSettings.maxPaymentAmount,
        autoCancelTimeout: platformSettings.autoCancelTimeout,
        dailyVoucherLimit: platformSettings.dailyVoucherLimit
      });
    }, [platformSettings]);

    const handleInputChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: parseFloat(value) || 0
      }));
    };

    const handleSaveSettings = async () => {
      const settings = {
        ...formData,
        platformFeePercentage: formData.platformFeePercentage / 100
      };
      await savePlatformSettings(settings);
    };

    const inputStyle = {
      width: '100%',
      padding: '10px',
      backgroundColor: '#374151',
      border: '1px solid #6B7280',
      borderRadius: '6px',
      color: 'white'
    };

    return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
      
      {/* SMS Protection Settings with working HTTP version */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>SMS Protection Settings</h3>
        
        {/* Current Status */}
        <div style={{
          padding: '16px',
          backgroundColor: '#374151',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `2px solid ${smsProtectionEnabled ? '#EF4444' : '#10B981'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '18px' }}>Protection Status:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: smsProtectionEnabled ? '#EF4444' : '#10B981'
              }}></div>
              <span style={{
                color: smsProtectionEnabled ? '#EF4444' : '#10B981',
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                {smsProtectionEnabled ? 'ENABLED' : 'DISABLED'}
              </span>
            </div>
          </div>
          
          {smsProtectionLastModified && (
            <div style={{ fontSize: '14px', color: '#9CA3AF' }}>
              <div>Last changed: {formatDate(smsProtectionLastModified)}</div>
              {smsProtectionModifiedBy && <div>Changed by: {smsProtectionModifiedBy}</div>}
            </div>
          )}
        </div>

        {/* Toggle Button */}
        <button
          onClick={handleSMSProtectionToggle}
          disabled={smsProtectionLoading}
          style={{
            width: '100%',
            padding: '16px 20px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: smsProtectionLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: '16px',
            backgroundColor: smsProtectionLoading
              ? '#6B7280'
              : smsProtectionEnabled
                ? '#10B981'
                : '#EF4444',
            opacity: smsProtectionLoading ? 0.7 : 1
          }}
        >
          {smsProtectionLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Updating...
            </div>
          ) : (
            `${smsProtectionEnabled ? 'DISABLE' : 'ENABLE'} SMS Protection`
          )}
        </button>

        {/* Status Message */}
        {smsProtectionStatus && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            marginBottom: '16px',
            backgroundColor: smsProtectionStatus.includes('Error') ? '#7F1D1D' : '#065F46',
            color: smsProtectionStatus.includes('Error') ? '#FECACA' : '#A7F3D0',
            border: `2px solid ${smsProtectionStatus.includes('Error') ? '#DC2626' : '#10B981'}`
          }}>
            {smsProtectionStatus}
          </div>
        )}

        {/* Explanation */}
        <div style={{
          fontSize: '13px',
          color: '#9CA3AF',
          lineHeight: '1.5',
          marginBottom: '16px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>What this controls:</div>
          <div style={{ marginBottom: '6px' }}>
            • <strong>ENABLED:</strong> SMS messages go through abuse protection (may be blocked for spam prevention)
          </div>
          <div>
            • <strong>DISABLED:</strong> All SMS messages sent directly without protection (for testing)
          </div>
        </div>

        {/* Warning when disabled */}
        {!smsProtectionEnabled && (
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#7C2D12',
            border: '2px solid #EA580C',
            marginTop: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>Warning</span>
              <div>
                <div style={{ color: '#FED7AA', fontWeight: 'bold', marginBottom: '4px' }}>
                  Testing Mode Active
                </div>
                <div style={{ fontSize: '14px', color: '#FED7AA', lineHeight: '1.4' }}>
                  SMS protection is disabled. All messages will be sent directly. 
                  Remember to re-enable protection for production use to prevent abuse.
                </div>
              </div>
            </div>
          </div>
        )}
        
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>

      {/* Console Debugging Toggle */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Developer Settings</h3>
        
        <div style={{
          padding: '16px',
          backgroundColor: '#374151',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `2px solid ${consoleEnabled ? '#10B981' : '#6B7280'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '18px' }}>Console Logging:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: consoleEnabled ? '#10B981' : '#6B7280'
              }}></div>
              <span style={{
                color: consoleEnabled ? '#10B981' : '#6B7280',
                fontWeight: 'bold'
              }}>
                {consoleEnabled ? 'ENABLED' : 'DISABLED'}
              </span>
            </div>
          </div>
          
          <div style={{ fontSize: '14px', color: '#9CA3AF' }}>
            Console logging is currently {consoleEnabled ? 'enabled' : 'disabled'} in production
          </div>
        </div>

        <button
          onClick={() => {
            const newState = !consoleEnabled;
            localStorage.setItem('adminConsoleEnabled', newState.toString());
            setConsoleEnabled(newState);
            window.location.reload(); // Reload to apply the change
          }}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: consoleEnabled ? '#DC2626' : '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '16px'
          }}
        >
          {consoleEnabled ? 'DISABLE' : 'ENABLE'} Console Logging
        </button>

        <div style={{
          fontSize: '13px',
          color: '#9CA3AF',
          lineHeight: '1.5',
          marginBottom: '16px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>What this controls:</div>
          <div style={{ marginBottom: '6px' }}>
            • <strong>ENABLED:</strong> Console logs are visible in production (for debugging)
          </div>
          <div>
            • <strong>DISABLED:</strong> Console logs are hidden in production (for security)
          </div>
        </div>

        {consoleEnabled && (
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#7C2D12',
            border: '2px solid #EA580C'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>Warning</span>
              <div>
                <div style={{ color: '#FED7AA', fontWeight: 'bold', marginBottom: '4px' }}>
                  Debug Mode Active
                </div>
                <div style={{ fontSize: '14px', color: '#FED7AA', lineHeight: '1.4' }}>
                  Console logging is enabled. Remember to disable this when done debugging to prevent sensitive data exposure.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Platform Settings</h3>
        {settingsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #7C3AED',
              borderTop: '4px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <p style={{ color: '#9CA3AF', marginTop: '16px' }}>Loading settings...</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Platform Fee Percentage (%)
              </label>
              <input 
                type="number" 
                min="0"
                max="50"
                step="0.1"
                value={formData.platformFeePercentage}
                onChange={(e) => handleInputChange('platformFeePercentage', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Minimum Payment Amount ($)
              </label>
              <input 
                type="number" 
                min="1"
                max="100"
                value={formData.minPaymentAmount}
                onChange={(e) => handleInputChange('minPaymentAmount', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Maximum Payment Amount ($)
              </label>
              <input 
                type="number" 
                min="10"
                max="1000"
                value={formData.maxPaymentAmount}
                onChange={(e) => handleInputChange('maxPaymentAmount', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Auto-cancel Timeout (hours)
              </label>
              <input 
                type="number" 
                min="1"
                max="72"
                value={formData.autoCancelTimeout}
                onChange={(e) => handleInputChange('autoCancelTimeout', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Daily Voucher Limit per Musician
              </label>
              <input 
                type="number" 
                min="1"
                max="20"
                value={formData.dailyVoucherLimit}
                onChange={(e) => handleInputChange('dailyVoucherLimit', e.target.value)}
                style={inputStyle}
              />
            </div>
            
            {console.log('About to render save button...')}

            <button
              onClick={handleSaveSettings}
              disabled={settingsSaving}
              style={{
                marginTop: '20px',
                padding: '15px 30px',
                backgroundColor: settingsSaving ? '#6B7280' : '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: settingsSaving ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                width: '100%'
              }}
            >
              {settingsSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>System Configuration</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#E5E7EB' }}>SMS Notifications</span>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              Enabled
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#E5E7EB' }}>Email Notifications</span>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              Enabled
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#E5E7EB' }}>Maintenance Mode</span>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#EF4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              Disabled
            </button>
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Database Statistics</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#3B82F6', fontSize: '24px', fontWeight: 'bold' }}>{requests.length}</div>
            <div style={{ color: '#9CA3AF', fontSize: '14px' }}>Total Requests</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#10B981', fontSize: '24px', fontWeight: 'bold' }}>{musicians.length}</div>
            <div style={{ color: '#9CA3AF', fontSize: '14px' }}>Total Musicians</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#F59E0B', fontSize: '24px', fontWeight: 'bold' }}>{songs.length}</div>
            <div style={{ color: '#9CA3AF', fontSize: '14px' }}>Total Songs</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#EC4899', fontSize: '24px', fontWeight: 'bold' }}>{vouchers.length}</div>
            <div style={{ color: '#9CA3AF', fontSize: '14px' }}>Total Vouchers</div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#111827',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '8px solid #7C3AED',
            borderTop: '8px solid #C4B5FD',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <p style={{ color: '#C4B5FD', fontSize: '20px', fontWeight: 'bold' }}>Loading admin dashboard...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#111827',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Notification Banner */}
      <NotificationBanner />

      {/* Header */}
      <div style={{
        backgroundColor: '#1F2937',
        padding: '20px 32px',
        borderBottom: '2px solid #374151'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            RequestASong Admin Dashboard
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ color: '#9CA3AF', fontSize: '14px' }}>
              Admin Controls • {analytics.timeRange}
            </div>
            <button style={{
              padding: '8px 16px',
              backgroundColor: '#EF4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs - Updated to include Gamification */}
      <div style={{ padding: '20px 32px', borderBottom: '1px solid #374151' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[
            { id: 'overview', label: 'Financial Overview' },
            { id: 'requests', label: 'Requests' },
            { id: 'musicians', label: 'Musicians' },
            { id: 'analytics', label: 'Analytics' },
            { id: 'aifraud', label: 'AI Fraud Detection' },
            { id: 'gamification', label: 'Gamification' },
            { id: 'referrals', label: 'Referral Settings' },
            { id: 'settings', label: 'Settings' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={tabStyle(activeTab === tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '32px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'requests' && <RequestsTab />}
        {activeTab === 'musicians' && <MusiciansTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'aifraud' && <AIFraudDashboard />}
        {activeTab === 'gamification' && <GamificationAdmin />}
        {activeTab === 'referrals' && <ReferralSystemSettings />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>

      {/* Edit Musician Profile Modal */}
      {editMusicianModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            border: '2px solid #374151',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 24px 0', color: '#E5E7EB' }}>
              Edit Musician Profile: {editMusicianModal.musician?.displayName || 'Unknown'}
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
              
              {/* Real Name field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Real Name
                </label>
                <input
                  type="text"
                  value={editMusicianData.realName || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, realName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ 
                  color: '#9CA3AF', 
                  fontSize: '12px', 
                  margin: '4px 0 0 0',
                  fontStyle: 'italic'
                }}>
                  For records only - never shown publicly
                </p>
              </div>

              {/* Stage Name field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Stage Name
                </label>
                <input
                  type="text"
                  value={editMusicianData.displayName || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, displayName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Email field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={editMusicianData.email || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Phone Number field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={editMusicianData.phoneNumber || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Genre field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Genre
                </label>
                <input
                  type="text"
                  value={editMusicianData.genre || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, genre: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Location field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Location
                </label>
                <input
                  type="text"
                  value={editMusicianData.location || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, location: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Average Rating field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Average Rating
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={editMusicianData.averageRating || 0}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, averageRating: parseFloat(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Total Ratings field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Total Ratings
                </label>
                <input
                  type="number"
                  min="0"
                  value={editMusicianData.totalRatings || 0}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, totalRatings: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Total Earnings field */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Total Earnings ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editMusicianData.totalEarnings || 0}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, totalEarnings: parseFloat(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ 
                  color: '#F59E0B', 
                  fontSize: '12px', 
                  margin: '4px 0 0 0',
                  fontStyle: 'italic'
                }}>
                  Warning: Use AdminBoostEarnings component for safe earnings adjustments
                </p>
              </div>

              {/* Bio field */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Bio
                </label>
                <textarea
                  value={editMusicianData.bio || ''}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, bio: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                  placeholder="Tell us about this musician..."
                />
              </div>

              {/* Status field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Status
                </label>
                <select
                  value={editMusicianData.status || 'active'}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, status: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              {/* Is Active field */}
              <div>
                <label style={{
                  display: 'block',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  Account Active
                </label>
                <select
                  value={editMusicianData.isActive ? 'true' : 'false'}
                  onChange={(e) => setEditMusicianData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#374151',
                    border: '1px solid #6B7280',
                    borderRadius: '6px',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setEditMusicianModal({ open: false, musician: null });
                  setEditMusicianData({});
                }}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveMusicianProfile}
                disabled={actionLoading.edit_musician_profile}
                style={{
                  flex: 2,
                  padding: '12px 20px',
                  backgroundColor: actionLoading.edit_musician_profile ? '#6B7280' : '#7C3AED',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: actionLoading.edit_musician_profile ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: actionLoading.edit_musician_profile ? 0.7 : 1
                }}
              >
                {actionLoading.edit_musician_profile ? 'Saving...' : 'Save Profile Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            border: '2px solid #374151',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>Request Details</h3>
            <div style={{ marginBottom: '20px' }}>
              <p><strong>Song:</strong> {selectedRequest.songName}</p>
              <p><strong>Artist:</strong> {selectedRequest.artist}</p>
              <p><strong>Customer:</strong> {selectedRequest.customerName}</p>
              <p><strong>Musician:</strong> {selectedRequest.musicianName}</p>
              <p><strong>Amount:</strong> {formatCurrency(selectedRequest.amount)}</p>
              <p><strong>Status:</strong> {selectedRequest.status}</p>
              <p><strong>Time:</strong> {formatTimestamp(selectedRequest.timestamp)}</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => setSelectedRequest(null)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              {selectedRequest.status !== 'cancelled' && (
                <button 
                  onClick={() => cancelRequestWithRefund(selectedRequest.id, selectedRequest.paymentIntentId)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: '#EF4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel & Refund
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Musician Detail Modal */}
      {selectedMusician && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            border: '2px solid #374151',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>
              Musician Profile: {selectedMusician.displayName || 'Unknown'}
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <p><strong>Real Name:</strong> {selectedMusician.realName || 'Not provided'}</p>
              <p><strong>Email:</strong> {selectedMusician.email}</p>
              <p><strong>Phone:</strong> {selectedMusician.phoneNumber || 'Not provided'}</p>
              <p><strong>Genre:</strong> {selectedMusician.genre || 'Not specified'}</p>
              <p><strong>Location:</strong> {selectedMusician.location || 'Not specified'}</p>
              <p><strong>Bio:</strong> {selectedMusician.bio || 'No bio available'}</p>
              <p><strong>Total Earnings:</strong> {formatCurrency(selectedMusician.totalEarnings || 0)}</p>
              <p><strong>Average Rating:</strong> {selectedMusician.averageRating || 'No ratings'}</p>
              <p><strong>Total Ratings:</strong> {selectedMusician.totalRatings || 0}</p>
              <p><strong>Status:</strong> {selectedMusician.status || 'active'}</p>
            </div>
            <button 
              onClick={() => setSelectedMusician(null)}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: '#6B7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            border: '2px solid #374151',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#E5E7EB' }}>
              Send Notification to {notificationModal.musicianName}
            </h3>
            <textarea
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="Enter your message here..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#374151',
                border: '1px solid #6B7280',
                borderRadius: '6px',
                color: 'white',
                marginBottom: '20px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => {
                  setNotificationModal({ open: false, musicianId: null, musicianName: '' });
                  setNotificationMessage('');
                }}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={sendNotificationToMusician}
                disabled={actionLoading.send_notification}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: actionLoading.send_notification ? '#6B7280' : '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: actionLoading.send_notification ? 'not-allowed' : 'pointer',
                  opacity: actionLoading.send_notification ? 0.7 : 1
                }}
              >
                {actionLoading.send_notification ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminDashboard;